import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { SEO } from '@/components/SEO';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Code, Key, Database, Zap, Shield, Lock } from 'lucide-react';
import logo from '@/assets/logo.png';
import { useAuth } from '@/contexts/AuthContext';
import { usePlanLimits } from '@/hooks/usePlanLimits';

export default function APIReference() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { canAccess, nextTierPrice, tier } = usePlanLimits();
  const hasAPIAccess = canAccess('hasAPIAccess');

  const endpoints = [
    {
      method: "POST",
      path: "/api/transactions/import",
      description: "Import bank transactions in bulk",
      auth: "Required",
      rateLimit: "100 requests/minute"
    },
    {
      method: "POST",
      path: "/api/invoices/analyze",
      description: "Submit invoice for AI analysis",
      auth: "Required",
      rateLimit: "50 requests/minute"
    },
    {
      method: "GET",
      path: "/api/reconciliation/matches",
      description: "Get reconciliation matches",
      auth: "Required",
      rateLimit: "200 requests/minute"
    },
    {
      method: "POST",
      path: "/api/reconciliation/execute",
      description: "Execute reconciliation matching",
      auth: "Required",
      rateLimit: "10 requests/minute"
    },
    {
      method: "GET",
      path: "/api/reports/generate",
      description: "Generate financial reports",
      auth: "Required",
      rateLimit: "20 requests/minute"
    }
  ];

  return (
    <>
      <SEO
        title="API Reference - Developer Documentation | Sentinel"
        description="Complete API reference for Sentinel. Integrate financial reconciliation into your applications with our RESTful API."
      />
      
      <div className="min-h-screen bg-background">
        <nav className="border-b border-border/50 bg-background/95 backdrop-blur-xl sticky top-0 z-50">
          <div className="container mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
                <img src={logo} alt="SENTINEL Logo" className="w-10 h-10 brightness-0 dark:brightness-100 dark:invert" />
                <span className="text-2xl font-bold">SENTINEL</span>
              </div>
              <div className="flex items-center gap-4">
                <ThemeToggle />
                <Button onClick={() => navigate('/auth')}>Get API Key</Button>
              </div>
            </div>
          </div>
        </nav>

        <div className="container mx-auto px-6 py-12 max-w-7xl">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold mb-4 pb-2">API Reference</h1>
            <p className="text-xl text-muted-foreground mb-8">
              Integrate Sentinel's powerful reconciliation engine into your applications
            </p>
            <div className="flex items-center justify-center gap-4">
              <Badge variant="outline" className="text-sm px-4 py-2">
                <Code className="w-4 h-4 mr-2" />
                REST API
              </Badge>
              <Badge variant="outline" className="text-sm px-4 py-2">
                <Key className="w-4 h-4 mr-2" />
                API Key Auth
              </Badge>
              <Badge variant="outline" className="text-sm px-4 py-2">
                <Shield className="w-4 h-4 mr-2" />
                Rate Limited
              </Badge>
            </div>
          </div>

          {/* API Access Gate */}
          {user && !hasAPIAccess && (
            <Card className="border-primary/50 bg-primary/5 mb-8">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row items-center gap-4">
                  <Lock className="h-12 w-12 text-primary shrink-0" />
                  <div className="text-center sm:text-left flex-1">
                    <h3 className="text-lg font-bold text-foreground mb-1">API Access - Pro Feature</h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      You're currently on the {tier} plan. Upgrade to Pro or Business to access the Sentinel API with up to 1,000 API calls/day.
                    </p>
                    <Button onClick={() => navigate('/pricing')}>
                      Upgrade to Pro - {nextTierPrice}/month
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Quick Start */}
          <Card className="p-8 mb-12 border-primary/20 bg-surface">
            <h2 className="text-2xl font-bold mb-4 pb-1 flex items-center gap-2">
              <Zap className="w-6 h-6 text-primary" />
              Quick Start
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">1. Get Your API Key</h3>
                <p className="text-muted-foreground mb-3">Sign in to your dashboard and navigate to Settings → API Keys</p>
                <code className="block bg-background p-4 rounded text-sm">
                  BASE_URL: https://api.sentinel.com/v1
                </code>
              </div>
              
              <div>
                <h3 className="font-semibold mb-2">2. Authentication</h3>
                <p className="text-muted-foreground mb-3">Include your API key in the Authorization header</p>
                <code className="block bg-background p-4 rounded text-sm">
                  Authorization: Bearer YOUR_API_KEY
                </code>
              </div>
            </div>
          </Card>

          {/* Endpoints */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold mb-6 pb-1 flex items-center gap-2">
              <Database className="w-8 h-8 text-primary" />
              Endpoints
            </h2>
            
            <div className="space-y-4">
              {endpoints.map((endpoint, index) => (
                <Card key={index} className="p-6 border-border/50">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                    <div className="flex items-center gap-3">
                      <Badge variant={endpoint.method === "POST" ? "default" : "secondary"}>
                        {endpoint.method}
                      </Badge>
                      <code className="text-sm font-mono">{endpoint.path}</code>
                    </div>
                    <div className="flex gap-2">
                      <Badge variant="outline">{endpoint.auth}</Badge>
                      <Badge variant="outline">{endpoint.rateLimit}</Badge>
                    </div>
                  </div>
                  <p className="text-muted-foreground">{endpoint.description}</p>
                </Card>
              ))}
            </div>
          </div>

          {/* Code Examples */}
          <Card className="p-8 mb-12">
            <h2 className="text-2xl font-bold mb-6 pb-1">Code Examples</h2>
            
            <Tabs defaultValue="javascript" className="w-full">
              <TabsList className="mb-4">
                <TabsTrigger value="javascript">JavaScript</TabsTrigger>
                <TabsTrigger value="python">Python</TabsTrigger>
                <TabsTrigger value="curl">cURL</TabsTrigger>
              </TabsList>
              
              <TabsContent value="javascript">
                <pre className="bg-background p-6 rounded-lg overflow-x-auto">
                  <code className="text-sm">
{`// Import transactions
const response = await fetch('https://api.sentinel.com/v1/transactions/import', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    transactions: [
      {
        date: '2025-01-15',
        amount: 25000,
        narration: 'Payment to vendor',
        bank_name: 'HDFC'
      }
    ]
  })
});

const data = await response.json();
console.log(data);`}
                  </code>
                </pre>
              </TabsContent>
              
              <TabsContent value="python">
                <pre className="bg-background p-6 rounded-lg overflow-x-auto">
                  <code className="text-sm">
{`import requests

# Import transactions
url = 'https://api.sentinel.com/v1/transactions/import'
headers = {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
}
data = {
    'transactions': [
        {
            'date': '2025-01-15',
            'amount': 25000,
            'narration': 'Payment to vendor',
            'bank_name': 'HDFC'
        }
    ]
}

response = requests.post(url, headers=headers, json=data)
print(response.json())`}
                  </code>
                </pre>
              </TabsContent>
              
              <TabsContent value="curl">
                <pre className="bg-background p-6 rounded-lg overflow-x-auto">
                  <code className="text-sm">
{`curl -X POST https://api.sentinel.com/v1/transactions/import \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "transactions": [
      {
        "date": "2025-01-15",
        "amount": 25000,
        "narration": "Payment to vendor",
        "bank_name": "HDFC"
      }
    ]
  }'`}
                  </code>
                </pre>
              </TabsContent>
            </Tabs>
          </Card>

          {/* Error Codes */}
          <Card className="p-8 mb-12">
            <h2 className="text-2xl font-bold mb-6 pb-1">HTTP Status Codes</h2>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Badge variant="outline" className="w-16">200</Badge>
                <span>Success - Request completed successfully</span>
              </div>
              <div className="flex items-center gap-3">
                <Badge variant="outline" className="w-16">400</Badge>
                <span>Bad Request - Invalid parameters</span>
              </div>
              <div className="flex items-center gap-3">
                <Badge variant="outline" className="w-16">401</Badge>
                <span>Unauthorized - Invalid or missing API key</span>
              </div>
              <div className="flex items-center gap-3">
                <Badge variant="outline" className="w-16">429</Badge>
                <span>Too Many Requests - Rate limit exceeded</span>
              </div>
              <div className="flex items-center gap-3">
                <Badge variant="outline" className="w-16">500</Badge>
                <span>Server Error - Contact support if persists</span>
              </div>
            </div>
          </Card>

          {/* CTA */}
          <Card className="p-12 text-center bg-gradient-primary border-0">
            <h2 className="text-4xl font-bold mb-4 pb-1 text-white">Ready to Build?</h2>
            <p className="text-xl text-white/90 mb-8">
              Get your API key and start integrating today
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="secondary" onClick={() => navigate('/auth')}>
                Get API Key
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate('/documentation')} className="text-white border-white hover:bg-white/10">
                View Documentation
              </Button>
            </div>
          </Card>
        </div>

        <footer className="border-t border-border/50 py-8 bg-surface/30">
          <div className="container mx-auto px-6 text-center text-sm text-muted-foreground">
            <p>&copy; 2025 SENTINEL. All rights reserved.</p>
          </div>
        </footer>
      </div>
    </>
  );
}
